import React from 'react';
import { Link } from 'react-router-dom';

import RRLogo from '../../design/components/RRLogo';
import './showcase.css';

/**
 * Brand & surfaces showcase — ported from the Claude Design handoff bundle
 * (`reciperack/project/Showcase.html`). Lives at /showcase. Iframes point at
 * the existing app (`/`) until the redesigned Web App + Recipe Rack
 * prototype routes land in their own PRs; swap those `src` values then.
 */
const Showcase: React.FC = () => {
  const APP_URL = '/';
  const COOK_URL = '/'; // swap when /cook lands

  return (
    <div className="rr-showcase">
      <main>
        {/* ============== TOP BAR ============== */}
        <div className="rr-topbar">
          <div className="rr-lockup-h">
            <span style={{ width: 32, height: 32, display: 'inline-block' }}>
              <RRLogo shelf="mixed" />
            </span>
            <span className="name">Recipe Rack</span>
          </div>
          <div className="links">
            <Link to="/">Web app</Link>
            <a href="#identity">Identity</a>
            <a href="#surfaces">Surfaces</a>
            <a href="#continuity">Continuity</a>
          </div>
        </div>

        {/* ============== HERO ============== */}
        <section className="rr-hero">
          <div className="stamp">v 1.0 · Brand &amp; Surfaces</div>
          <h1>
            The shelf where
            <br />
            your <em>cooking</em> lives.
          </h1>
          <p className="lede">
            A unified mockup of Recipe Rack — one identity, two surfaces. Paste a URL on the
            web, save it to your shelf, cook from your phone with messy hands.
          </p>
        </section>

        {/* ============== IDENTITY ============== */}
        <section className="rr-section" id="identity">
          <div className="rr-section-head">
            <div className="num">01 — Identity</div>
            <h2>One mark, three temperatures.</h2>
            <p>
              The wordmark sits in Newsreader 500 — a working-cookbook serif. The mark itself
              is three hairline shelves with jars, plates, and exactly one tomato — the recipe
              you're cooking right now.
            </p>
          </div>
          <hr className="rr-rule" />

          <div className="rr-id-grid">
            {/* Primary lockup */}
            <div className="card-frame">
              <span className="label">Primary lockup · ink on paper</span>
              <div className="rr-lockup-stacked">
                <div style={{ width: 88, height: 88 }}>
                  <RRLogo shelf="mixed" />
                </div>
                <span
                  style={{
                    fontFamily: 'var(--display)',
                    fontWeight: 500,
                    fontSize: 32,
                    letterSpacing: '-0.025em',
                  }}
                >
                  Recipe<span className="accent">·</span>Rack
                </span>
              </div>
            </div>

            {/* Mark on dark */}
            <div className="card-frame card-dark">
              <span className="label">Mark · reverse</span>
              <div style={{ width: 120, height: 120 }}>
                <RRLogo shelf="mixed" variant="reverse" />
              </div>
            </div>

            {/* App icon */}
            <div className="card-frame card-cream">
              <span className="label">App icon · 1024px</span>
              <div className="app-icon">
                <div style={{ width: 92, height: 92 }}>
                  <RRLogo shelf="mixed" variant="icon" />
                </div>
              </div>
            </div>
          </div>

          {/* Color strip */}
          <div className="rr-strip">
            <div className="rr-swatch" style={{ background: 'var(--paper)' }}>
              <span className="role" style={{ color: 'var(--ink-soft)' }}>Surface · paper</span>
              <div>
                <div className="nm">Paper</div>
                <div className="hex">#FBF7EE</div>
              </div>
            </div>
            <div
              className="rr-swatch"
              style={{ background: 'var(--ink)', color: '#fff', borderColor: 'var(--ink)' }}
            >
              <span className="role">Type · ink</span>
              <div>
                <div className="nm">Ink</div>
                <div className="hex" style={{ opacity: 0.7 }}>#1F2024</div>
              </div>
            </div>
            <div
              className="rr-swatch"
              style={{ background: 'var(--tomato)', color: '#fff', borderColor: 'var(--tomato)' }}
            >
              <span className="role">Action · accent</span>
              <div>
                <div className="nm">Tomato</div>
                <div className="hex" style={{ opacity: 0.8 }}>#C7553B</div>
              </div>
            </div>
            <div className="rr-swatch" style={{ background: 'var(--sage)' }}>
              <span className="role" style={{ color: 'var(--ink-soft)' }}>Rack · grouping</span>
              <div>
                <div className="nm">Sage</div>
                <div className="hex">#A8B89C</div>
              </div>
            </div>
          </div>

          {/* Type strip */}
          <div className="rr-type-grid">
            <div className="rr-type-card">
              <span className="role">Display · Newsreader</span>
              <div className="specimen">Bake.</div>
              <div className="meta">
                Working-cookbook serif. Used for titles, recipe names, the wordmark, and
                editorial moments. 400 / 500 / italic.
              </div>
            </div>
            <div className="rr-type-card sans">
              <span className="role">Sans · DM Sans</span>
              <div className="specimen">All your recipes</div>
              <div className="meta">
                UI workhorse. Buttons, labels, body copy, microcopy. 400 / 500 / 600 / 700.
              </div>
            </div>
            <div className="rr-type-card mono">
              <span className="role">Mono · Space Mono</span>
              <div className="specimen">350g · 02</div>
              <div className="meta">
                Quantities, step numbers, source domains. Never for prose. 400 / 700.
              </div>
            </div>
          </div>
        </section>

        {/* ============== SURFACES — HERO ============== */}
        <section className="rr-section" id="surfaces">
          <div className="rr-section-head">
            <div className="num">02 — Surfaces</div>
            <h2>Two screens, one shelf.</h2>
            <p>
              The same library, sitting in your browser tab and in your pocket. Paste from
              your laptop while you're saving links — cook from the phone propped on the
              counter. Live previews below.
            </p>
          </div>
          <hr className="rr-rule" />

          <div className="rr-surfaces-hero">
            <div className="copy">
              <div className="eyebrow">Recipe Rack · Web + Mobile</div>
              <h2>Cook on the phone you've got grease on.</h2>
              <p>
                Big type, two thumb-zones, a single-finger tap to advance steps. The web is
                for collecting; the phone is for cooking. The shelf is one and the same.
              </p>
            </div>
            <div className="stage">
              <div className="rr-browser" aria-label="Web app preview">
                <div className="rr-browser-bar">
                  <div className="dots">
                    <i />
                    <i />
                    <i />
                  </div>
                  <div className="url">reciperack.app/library</div>
                </div>
                <div className="frame-wrap">
                  <iframe src={APP_URL} loading="lazy" title="Recipe Rack web app" />
                </div>
              </div>
              <div className="rr-phone" aria-label="Mobile app preview">
                <div className="frame-wrap">
                  <iframe src={APP_URL} loading="lazy" title="Recipe Rack mobile app" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============== PAIRINGS ============== */}
        <section className="rr-section" id="continuity">
          <div className="rr-section-head">
            <div className="num">03 — Continuity</div>
            <h2>Same vocabulary, both surfaces.</h2>
            <p>
              Hairline shelves, ink-on-paper cards, one tomato accent, the same Newsreader
              display. The web stretches into rack rows; the phone folds into a single
              thumb-friendly column.
            </p>
          </div>
          <hr className="rr-rule" />

          {/* Pair 1: Library */}
          <div className="rr-pair">
            <div>
              <div className="rr-pair-head">
                <div className="num">A — Library</div>
                <h3>Your shelf, end to end.</h3>
                <p>
                  On desktop, racks render as horizontal chapters with hairline rules and
                  tomato accent bars. On mobile, the same racks become vertical sections you
                  scroll through one-handed. Same sage dot, same hairline rule, same
                  hierarchy.
                </p>
              </div>
              <div className="rr-pair-web">
                <div className="top">
                  <i />
                  <i />
                  <i />
                </div>
                <div className="frame-wrap">
                  <iframe src={APP_URL} loading="lazy" title="Web library" />
                </div>
              </div>
            </div>
            <div className="rr-pair-mob">
              <div className="frame-wrap">
                <iframe src={APP_URL} loading="lazy" title="Mobile library" />
              </div>
            </div>
          </div>

          {/* Pair 2: Cook mode */}
          <div className="rr-pair flip">
            <div className="rr-pair-mob">
              <div className="frame-wrap">
                <iframe src={COOK_URL} loading="lazy" title="Mobile cook mode" />
              </div>
            </div>
            <div>
              <div className="rr-pair-head">
                <div className="num">B — Cook mode</div>
                <h3>Big type, one tap, no scroll.</h3>
                <p>
                  Cook mode collapses the recipe to one step at a time, in 56px Newsreader,
                  with a single saffron CTA the width of your thumb. Ingredients live one
                  tap behind a drawer. Same on web (full-screen overlay) and mobile (full
                  sheet).
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============== MANTRA ============== */}
        <section className="rr-mantra">
          <p className="word">
            Buy now, <em>cook</em> later.
          </p>
          <div className="sub">Recipe Rack · keep what you'll cook · forget the rest</div>
        </section>

        <footer className="rr-footer">
          <span>© Recipe Rack — design v1.0, May 2026</span>
          <span className="mono">paper #FBF7EE · ink #1F2024 · tomato #C7553B</span>
        </footer>
      </main>
    </div>
  );
};

export default Showcase;
